import React, { useState } from 'react';
import { cn } from '@/lib/utils';

interface ProductImageProps {
  src: string;
  alt: string;
  className?: string;
  fallbackSrc?: string;
  aspectRatio?: 'square' | 'portrait' | 'landscape';
  showLoadingSpinner?: boolean;
}

const ProductImage: React.FC<ProductImageProps> = ({
  src,
  alt,
  className = '',
  fallbackSrc = '/placeholder.svg',
  aspectRatio = 'square',
  showLoadingSpinner = true
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const handleLoad = () => {
    setIsLoading(false);
  };

  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setHasError(true);
    setIsLoading(false);
    e.currentTarget.src = fallbackSrc;
  };

  const aspectRatioClasses = {
    square: 'aspect-square',
    portrait: 'aspect-[3/4]',
    landscape: 'aspect-[4/3]'
  };

  return (
    <div className={cn('relative overflow-hidden', aspectRatioClasses[aspectRatio], className)}>
      {/* Loading State */}
      {isLoading && showLoadingSpinner && (
        <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
          <div className="relative">
            {/* Animated Placeholder */}
            <div className="w-8 h-8 bg-gradient-to-br from-jamaica-green/20 to-jamaica-green/10 rounded-full animate-pulse"></div>
            {/* Spinning Ring */}
            <div className="absolute inset-0 w-8 h-8 border-2 border-jamaica-green/20 border-t-jamaica-green/60 rounded-full animate-spin"></div>
          </div>
        </div>
      )}
      
      {/* Shimmer Effect for Loading */}
      {isLoading && (
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full animate-shimmer"></div>
      )}

      {/* Actual Image */}
      <img
        src={src}
        alt={alt}
        onLoad={handleLoad}
        onError={handleError}
        className={cn(
          'w-full h-full object-cover transition-opacity duration-300',
          isLoading ? 'opacity-0' : 'opacity-100'
        )}
      />
    </div>
  );
};

export default ProductImage; 